import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Page from '../../../components/page/Page';
import { MEMBERSHIP } from '../../../constants/membership';
import {
  alertWarning,
  hideModal,
  setMembershipPriceId,
} from '../../../redux/actions';
import text from '../../../text';
import { getUserMembership } from '../../../utilities/user';
import MembershipInvoice from '../Invoice/MembershipInvoice';
import MembershipManagementWizard from '../MembershipWizard/MembershipManagementWizard';
import PaymentProvider from '../PaymentContext';
import { useQuery } from '../../../hooks/useQuery';
import { isRouteIncluded } from '../../../utilities/routes';
import routes from '../../../routes/constants';
import { profileSelectors } from '../../../redux/selectors/profile';
import { useRedirect } from '../../../hooks/useRedirect';
import useMembershipButton from '../../../hooks/useMembershipButton';
import { START_TRIAL_MODAL } from '../../../components/modal/StartTrialModal/StartTrialModal';

const cleanRedirectPath = (path) => {
  if (!path) return null;
  try {
    // Handle paths that contain full URLs
    if (path.includes('://')) {
      const url = new URL(path);
      return url.pathname + url.search + url.hash;
    }
    return path;
  } catch (e) {
    console.error('Error parsing redirect path:', e);
    return path;
  }
};

const MembershipManagement = () => {
  const queryParams = useQuery();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => ({
    currentUser: state.profileReducer.userProfile,
  }));
  const [alertDispatched, setAlertDispatched] = useState(false);
  const isUpgradeRecommended = useSelector(
    profileSelectors.getIsUpgradeRecommended
  );
  const redirect = useRedirect();
  const { handleMembershipClick } = useMembershipButton();

  const view3dTooltipText = isUpgradeRecommended
    ? text('upgrade')
    : text('startNdayTrial', { n: 14 });

  const handleDispatch = useCallback(
    (membershipPriceId) => {
      dispatch(setMembershipPriceId(membershipPriceId));

      if (location.state && location.state.alert && !alertDispatched) {
        setAlertDispatched(true);

        Promise.resolve().then(() => {
          // deferred dispatch to wait state updates
          dispatch(alertWarning(location.state.alert));
        });
      }
    },
    [dispatch, location.state, alertDispatched]
  );

  useEffect(() => {
    const membershipInfo = getUserMembership(currentUser);

    let membershipPriceId;
    if (!membershipInfo && !isEmpty(currentUser)) {
      membershipPriceId = MEMBERSHIP.MONTHLY.PRICE_ID;
    } else {
      membershipPriceId = membershipInfo?.price_id;
    }

    handleDispatch(membershipPriceId);
  }, [handleDispatch, currentUser, history]);

  /**
   * Handle when redirectTo contains 3D viewer route.
   * This is to dispatch alert warning when the user cancels upgrade or go backs to dashboard
   */
  const redirectTo = useMemo(() => {
    const stateRedirect = location.state?.redirectTo;
    const queryRedirect = queryParams.get('redirectTo');

    if (!stateRedirect && !queryRedirect) return null;

    return stateRedirect
      ? {
          ...stateRedirect,
          path: cleanRedirectPath(stateRedirect.path),
        }
      : {
          path: cleanRedirectPath(queryRedirect),
          label: queryParams.get('label'),
        };
  }, [location.state?.redirectTo, queryParams]);

  const isViewer3dRoute = isRouteIncluded([routes.view3D.root]);

  const showMembershipAlert = useCallback(() => {
    dispatch(
      alertWarning(
        <span>
          <a
            onClick={() => {
              handleMembershipClick(
                {
                  redirectTo: {
                    path: routes.view3DExternal.root,
                    label: text('navigateToRoute', {
                      navigation: 'Continue',
                      routeName: text('viewer3D'),
                    }),
                  },
                },
                false,
                () => redirect.push(routes.view3DExternal.root),
                {
                  onHide: () => {
                    dispatch(hideModal(START_TRIAL_MODAL));
                    dispatch(
                      alertWarning(view3dTooltipText, {
                        className: 'v2 content-minus-sidenav',
                      })
                    );
                  },
                  onStartTrial: () => dispatch(hideModal(START_TRIAL_MODAL)),
                }
              );
            }}
            className='mr-1 white-underlined-link'
          >
            {view3dTooltipText}
          </a>
          {text('accessThe3dViewer')}
        </span>,
        {
          className: 'v2 content-minus-sidenav',
        }
      )
    );
  }, [dispatch, view3dTooltipText, handleMembershipClick, redirect]);

  const handleGoBack = useCallback(() => {
    if (redirectTo?.path && isViewer3dRoute(redirectTo.path)) {
      showMembershipAlert();
    }
    history.goBack();
  }, [redirectTo?.path, isViewer3dRoute, history, showMembershipAlert]);
  // end handle when redirectTo contains 3D viewer route

  return (
    <PaymentProvider>
      <Page className='MembershipManagement'>
        <div className='invoice-section'>
          <Button variant='link' onClick={handleGoBack}>
            <Icon icon='caret-left' />
            {text('back')}
          </Button>
          <MembershipInvoice />
        </div>
        <div className='section'>
          <MembershipManagementWizard />
        </div>
      </Page>
    </PaymentProvider>
  );
};

export default MembershipManagement;
